import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { FeatureService } from '../../core/tenant/feature.service';
import { SessionService } from '../../core/tenant/session.service';
import { TenantSession } from '../../core/tenant/feature.model';

/** Por qué no se pudo entrar. Se pinta en palabras; el código solo viaja en la URL. */
export type MotivoSinRuta = 'sesion-vieja' | 'rol';

/**
 * F6.3 — quién entra al Modo Ruta, y **qué se le dice al que no**.
 *
 * Antes esto eran `featureGuard('ruta')` + `rolGuard('vendedor','admin')`, y los dos mandaban a `/pos` sin decir
 * nada: el 2026-09-16 Santiago abrió `/ruta` en staging, la pantalla saltó a la caja y no había forma de saber por
 * qué. Los módulos de la sesión se guardan **al entrar**, así que una sesión abierta antes de que el servidor
 * sirviera `ruta` no lo trae, y eso no es un «no tienes permiso»: es «vuelve a entrar». Redirigir en silencio hace
 * que parezca lo primero.
 *
 * Reglas:
 *   - sin token → al login. Se mira el TOKEN y no `session()`: el token es lo que dice que alguien entró (hasta el
 *     18/09 el POS se inventaba una sesión «demo» sin nadie dentro, y la sesión nunca daba null);
 *   - con sesión y sin el módulo `ruta` → aviso «sesión vieja», que dice qué hacer;
 *   - con el módulo pero con otro rol (cajero) → aviso «rol». El administrador SÍ entra, en solo lectura (R1).
 */
export const puedeEntrarARuta: CanActivateFn = () => {
  const session = inject(SessionService);
  const features = inject(FeatureService);
  const router = inject(Router);

  if (!session.tieneToken()) {
    console.warn('[ruta] no se entra: no hay sesión iniciada (sin token guardado)');
    return router.createUrlTree(['/login']);
  }
  const rol = session.session()?.role ?? '';
  if (!features.has('ruta')) {
    // El motivo, escrito: esto dejó de ser una adivinanza el 2026-09-16, cuando /ruta rebotaba sin decir nada.
    console.warn(`[ruta] no se entra: la sesión no trae el módulo «ruta». Módulos: [${session.modules().join(', ')}]; rol: «${rol}»`);
    return router.createUrlTree(['/ruta/no-disponible'], { queryParams: { motivo: 'sesion-vieja' } });
  }
  if (rol !== 'vendedor' && rol !== 'admin') {
    console.warn(`[ruta] no se entra: el rol «${rol}» no está permitido (se esperan «vendedor» o «admin»)`);
    return router.createUrlTree(['/ruta/no-disponible'], { queryParams: { motivo: 'rol' } });
  }
  return true;
};

/**
 * A dónde entra cada uno al abrir el POS (ECM, #10 del 17/09): **nada de elegir por URL**.
 *
 * El vendedor con el módulo `ruta` entra directo a Ruta: es su pantalla, y el dueño del mayorista externo tuvo que
 * teclear `/ruta` a mano en el celular para llegar. El administrador y el cajero entran a la caja; el administrador
 * puede ir a Ruta a mirar, pero no es donde trabaja.
 */
export function destinoAlEntrar(sesion: TenantSession | null): '/ruta' | '/pos' | '/bodega' {
  // W1: el bodeguero no vende; entra al Modo Bodega siempre, y si el negocio no lo tiene, su guarda le dice por qué.
  if (sesion?.role === 'bodeguero') return '/bodega';
  return sesion?.role === 'vendedor' && sesion.modules.includes('ruta') ? '/ruta' : '/pos';
}
